import type { MemoExoticComponent } from 'react';
import React, { memo, useMemo, useState } from 'react';
import { ScreenSpaceFlagsPlacementComponent } from '@post-office-placement/screen-space-flags/placement';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import Placement from '@atlassian/post-office-placement/placement';
import { ScreenSpaceFlagPlacementPreviewSwitcher } from '@atlassian/post-office-placement/preview';
import {
	PlacementFetchProvider,
	usePlacementPreloadedData,
} from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { expVal } from '@confluence/feature-experiments';
import { ConditionalUfoHoldIgnore } from '@confluence/react-ufo-conditional-hold-ignore/entry-points/ConditionalUfoHoldIgnore.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { isRestrictedFromPostOfficeSynthetic } from './is-restricted-from-post-office-synthetic.ts';

export const ScreenSpaceFlagsPlacement: MemoExoticComponent<() => React.JSX.Element | null> = memo(
	(): React.JSX.Element | null => {
		const [messageErrorToReport, setMessageErrorToReport] = useState<any | undefined>();

		const [preloadedInitialData, setPreloadedInitialData] = usePlacementPreloadedData(
			'screen-space-flags/update-messages',
		);

		const memoizedPlacementProps = useMemo(
			() => ({
				reportExpectedError: markErrorAsHandled,
				reportUnexpectedError: setMessageErrorToReport,
				preloadedInitialData,
				setPreloadedInitialData,
			}),
			[preloadedInitialData, setPreloadedInitialData],
		);

		const { userId } = useSessionData();

		const isEmbeddedView = useIsEmbeddedConfluence_DO_NOT_USE();

		if (isEmbeddedView) {
			return null;
		}

		if (!userId) {
			return null;
		}

		// Suppress the placement (and its Placement API request) for non-Post-Office synthetic
		// test tenants, which otherwise generate large volumes of Placement API traffic and
		// trigger Stargate rate-limit alerts.
		if (isRestrictedFromPostOfficeSynthetic()) {
			return null;
		}

		const content = (
			<ScreenSpaceFlagPlacementPreviewSwitcher isEnabled>
				<PlacementFetchProvider>
					<Placement
						placementId="screen-space-flags"
						// @ts-ignore - Type 'ComponentType<ErrorBoundariedProps<Placement>>' is not assignable to type 'PlacementComponent<LocalPlacementProps>'
						PlacementComponent={ScreenSpaceFlagsPlacementComponent}
						placementProps={memoizedPlacementProps}
						disableInitialData
					/>

					{messageErrorToReport && <ErrorDisplay error={messageErrorToReport} />}
				</PlacementFetchProvider>
			</ScreenSpaceFlagPlacementPreviewSwitcher>
		);

		return expVal('confluence_edit_page_conditional_ufohold_ignore', 'isEnabled', false) ? (
			<ConditionalUfoHoldIgnore>{content}</ConditionalUfoHoldIgnore>
		) : (
			content
		);
	},
);
